/**
 * @file useNumberSlider.ts
 * @description Состояние и правила `NumberSliderField` без разметки (phase19.16,
 * партия SB120 «свобода выбора», этап 0; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §2): пределы в
 * показанных единицах (с `floor`), живое значение при перетаскивании,
 * черновик текста в поле, подпись-поправка, предпросмотр раз в кадр и запись
 * одним шагом отмены (отпустили, Enter, blur, ступень).
 */

import { useState, type KeyboardEvent } from "react";

import { useFramePreview } from "./useFramePreview";
import {
  DEFAULT_DISPLAY,
  formatNumber,
  parseNumberInput,
  resolveNumberInput,
  TYPED_DECIMALS,
  roundTo,
  snapToStep,
  speakValue,
  stepDecimals,
  toDisplay,
  toStored,
  type NumberDisplay,
  type NumberUnit,
} from "../../lib/site-fields/number-input";

export interface NumberSliderInput {
  value: number | undefined;
  inherit: { value?: number; label: string };
  min: number;
  max: number;
  step: number;
  largeStep?: number;
  unit: NumberUnit;
  display?: NumberDisplay;
  recommended?: { min: number; max: number; text: string };
  floor?: { value: number; reason: string };
  marker?: { value: number; label: string };
  onChange: (value: number | undefined) => void;
  onPreview?: (value: number | undefined) => void;
  /** Только целые (схема — int: толщина линии и т. п.): ввод округляется до целого. */
  integer?: boolean;
}

export function useNumberSlider(p: NumberSliderInput) {
  const d = p.display ?? DEFAULT_DISPLAY;
  const [live, setLive] = useState<number | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const preview = useFramePreview(p.onPreview);

  // Пределы и шаги в показанных единицах; закон (floor) поднимает нижний край.
  const shownStep = roundTo(p.step * d.factor, 6);
  // Знаков не меньше, чем у шага: шаг 0,5 при decimals 0 не округлится до целого.
  const decimals = Math.max(d.decimals, stepDecimals(shownStep));
  // Введённое руками не тянется к шагу (QA SB120 этап 1, D1): показываем и
  // храним до 2 знаков (30,72 остаётся 30,72); целое поле — без дробей.
  const typed = p.integer ? decimals : Math.max(decimals, TYPED_DECIMALS);
  const range = {
    min: toDisplay(p.floor ? Math.max(p.min, p.floor.value) : p.min, d),
    max: toDisplay(p.max, d),
    step: shownStep,
    decimals,
    unit: p.unit,
  };
  const large = p.largeStep !== undefined ? roundTo(p.largeStep * d.factor, 6) : range.step * 10;
  const inherited = p.value === undefined;
  const known = p.value ?? p.inherit.value;
  const clampShown = (v: number) => snapToStep(v, range.min, range.max, range.step);
  // Наследуемое без числа — бегунок у метки или посередине (полый, «не задано»).
  const rest = known !== undefined ? toDisplay(known, { ...d, decimals: typed }) : p.marker ? toDisplay(p.marker.value, d) : (range.min + range.max) / 2;
  // Старые данные — как есть (0,33 не превращается в 35 %), только в пределах.
  const shown = live ?? Math.min(range.max, Math.max(range.min, rest));
  const current = live !== null ? toStored(live, d) : p.value;
  const rec = p.recommended;
  const warning = rec && current !== undefined && (current < rec.min || current > rec.max) ? rec.text : null;

  const save = (stored: number | undefined) => {
    setDraft(null);
    if (stored !== p.value) {
      preview.cancel();
      p.onChange(stored);
    } else {
      // Отпустили на исходном: записи нет, но канва могла остаться на
      // промежуточном кадре — возвращаем её к значению.
      preview.now(p.value);
    }
  };
  const commitText = () => {
    if (draft === null) return;
    const result = resolveNumberInput(draft, range, typed);
    setNote(result.note);
    if (result.kind === "invalid") {
      setDraft(null);
      preview.now(p.value);
    } else save(toStored(result.value, d));
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") commitText();
    else if (event.key === "Escape") {
      setNote(null);
      setDraft(null);
      preview.now(p.value);
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const from = (draft !== null ? parseNumberInput(draft) : null) ?? shown;
      const delta = (event.shiftKey ? large : range.step) * (event.key === "ArrowUp" ? 1 : -1);
      const next = clampShown(from + delta);
      setDraft(formatNumber(next, decimals));
      preview.schedule(toStored(next, d));
    }
  };

  /** «24 пикселя» / «16 пикселей, как у колонки» / «как у файла». */
  const valueText = (v: number) => {
    const spoken = speakValue(v, decimals, p.unit);
    if (!inherited || live !== null) return spoken;
    return known !== undefined ? `${spoken}, ${p.inherit.label}` : p.inherit.label;
  };

  return {
    range,
    large,
    shown,
    inherited,
    /** Бегунок полый: значение не задано и его не тянут. */
    hollow: inherited && live === null,
    /** Наследуемое без известного числа — поле пустое. */
    unknown: inherited && known === undefined,
    warning,
    note,
    text: draft ?? (inherited && known === undefined ? "" : formatNumber(shown, typed)),
    editing: draft !== null,
    markerShown: p.marker ? toDisplay(p.marker.value, d) : undefined,
    valueText,
    onSlide: (v: number) => {
      setLive(v);
      setDraft(null);
      setNote(null);
      preview.schedule(toStored(v, d));
    },
    onSlideEnd: (v: number) => {
      setLive(null);
      save(toStored(v, d));
    },
    onType: (text: string) => {
      setDraft(text);
      setNote(null);
    },
    onInputKeyDown,
    onInputBlur: commitText,
    /** Ступень или «↺»: сразу запись. */
    pick: (stored: number | undefined) => {
      setNote(null);
      save(stored);
    },
  };
}
